import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import type { EnvConfig } from '../types/env';

// 等待 window.ENV 載入（優化版 - 更快的檢查頻率）
const waitForEnv = (): Promise<EnvConfig> => {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && window.ENV) {
      resolve(window.ENV as unknown as EnvConfig);
    } else {
      let attempts = 0;
      const maxAttempts = 50; // 最多嘗試 50 次 (2.5 秒)
      
      const checkEnv = () => {
        attempts++;
        if (typeof window !== 'undefined' && window.ENV) {
          console.log(`✅ ENV 載入成功 (嘗試 ${attempts} 次)`);
          resolve(window.ENV as unknown as EnvConfig);
        } else if (attempts >= maxAttempts) {
          console.warn('⚠️ ENV 載入超時，使用備用配置');
          // 使用備用配置（從 import.meta.env）
          resolve({
            VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://checkplay-230168256043.asia-east1.run.app',
            VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION || '0.1018.9',
          } as unknown as EnvConfig);
        } else {
          setTimeout(checkEnv, 50); // 從 100ms 優化為 50ms
        }
      };
      checkEnv();
    }
  });
};

// 初始化 Firebase 的函數
const initializeFirebase = async () => {
  const env = await waitForEnv();

  // 驗證 Firebase 配置
  console.log('🔍 Firebase 配置驗證:', {
    apiKey: env.VITE_FIREBASE_API_KEY ? '✅ 已設定' : '❌ 未設定',
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ? '✅ 已設定' : '❌ 未設定',
    projectId: env.VITE_FIREBASE_PROJECT_ID ? '✅ 已設定' : '❌ 未設定',
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET ? '✅ 已設定' : '❌ 未設定',
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID ? '✅ 已設定' : '❌ 未設定',
    appId: env.VITE_FIREBASE_APP_ID ? '✅ 已設定' : '❌ 未設定'
  });

  const firebaseConfig = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
    measurementId: env.VITE_FIREBASE_MEASUREMENT_ID,
  };

  // 檢查必要的配置是否存在
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    throw new Error('Firebase 配置不完整：缺少必要的 API Key 或 Project ID');
  }

  console.log('🚀 初始化 Firebase 應用...');
  let app;
  try {
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
      console.log('✅ Firebase 應用初始化成功');
    } else {
      app = getApp();
      console.log('✅ 使用現有的 Firebase 應用');
    }
  } catch (error) {
    console.error('❌ Firebase 應用初始化失敗:', error);
    throw error;
  }
  
  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app);

  console.log('✅ Firebase 服務初始化完成');
  return { app, auth, db, storage };
};

// 立即初始化並導出
const firebasePromise = initializeFirebase();

export const getFirebaseServices = () => firebasePromise;

// 為了向後兼容，導出預設的服務（但會是 Promise）
export const auth = firebasePromise.then(({ auth }) => auth);
export const db = firebasePromise.then(({ db }) => db);
export const storage = firebasePromise.then(({ storage }) => storage); 